import { useEffect, useState } from "react";

/**
 * Spotlight — draws a pulsing emerald frame around a DOM element,
 * with an optional label, for `durationMs` milliseconds. Auto-removes itself.
 *
 * Props:
 *   selector  : CSS selector for the target element
 *   label     : optional string label shown above the highlight
 *   durationMs: how long the highlight stays (default 3000)
 *   onDone    : callback when the highlight disappears
 */
export default function Spotlight({ selector, label, durationMs = 3000, onDone }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!selector) return undefined;

    const compute = () => {
      const el = document.querySelector(selector);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      // Scroll element into view if it's off-screen
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Recompute after scroll
        setTimeout(() => {
          const r2 = el.getBoundingClientRect();
          setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
        }, 400);
        return null;
      }
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    };

    // Retry a few times in case the target isn't mounted yet (navigation in progress)
    let attempts = 0;
    let id;
    const tryFind = () => {
      const r = compute();
      if (r) { setRect(r); return; }
      attempts += 1;
      if (attempts < 8) id = setTimeout(tryFind, 300);
    };
    tryFind();

    // Keep rect in sync with scroll & resize
    const refresh = () => {
      const el = document.querySelector(selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener("scroll", refresh, true);
    window.addEventListener("resize", refresh);

    // Auto-cleanup after durationMs
    const cleanup = setTimeout(() => {
      if (onDone) onDone();
    }, durationMs);

    return () => {
      if (id) clearTimeout(id);
      clearTimeout(cleanup);
      window.removeEventListener("scroll", refresh, true);
      window.removeEventListener("resize", refresh);
    };
  }, [selector, durationMs, onDone]);

  if (!rect) return null;
  // Pad the highlight slightly for visual breathing room
  const PAD = 6;
  const top = rect.top - PAD;
  const left = rect.left - PAD;
  const width = rect.width + PAD * 2;
  const height = rect.height + PAD * 2;

  // Position the label above the highlight, or below if there's no room above
  const labelAbove = top > 36;
  const labelStyle = labelAbove
    ? { top: top - 30, left: left + Math.max(0, width / 2 - 90), maxWidth: 180 }
    : { top: top + height + 8, left: left + Math.max(0, width / 2 - 90), maxWidth: 180 };

  return (
    <>
      {/* Pulsing emerald frame */}
      <div
        className="pointer-events-none fixed z-[9997] rounded-lg"
        style={{
          top,
          left,
          width,
          height,
          boxShadow:
            "0 0 0 2px #10b981, 0 0 0 6px rgba(16,185,129,0.25), 0 0 28px rgba(16,185,129,0.55)",
          animation: "spotlight-pulse 1.6s ease-in-out infinite",
          transition: "top 220ms ease-out, left 220ms ease-out, width 220ms ease-out, height 220ms ease-out",
        }}
        data-testid="demo-spotlight"
      />
      {/* Floating label */}
      {label ? (
        <div
          className="pointer-events-none fixed z-[9997] px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap shadow-lg"
          style={{
            ...labelStyle,
            background: "#047857",
            color: "white",
            animation: "spotlight-label-in 320ms ease-out both",
            transform: "translateX(0)",
          }}
          data-testid="demo-spotlight-label"
        >
          {label}
          {/* arrow */}
          <span
            style={{
              position: "absolute",
              left: 14,
              [labelAbove ? "bottom" : "top"]: -4,
              width: 8,
              height: 8,
              background: "#047857",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      ) : null}
      {/* Keyframes */}
      <style>{`
        @keyframes spotlight-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 2px #10b981,
              0 0 0 6px rgba(16,185,129,0.18),
              0 0 24px rgba(16,185,129,0.45);
          }
          50% {
            box-shadow:
              0 0 0 2px #10b981,
              0 0 0 10px rgba(16,185,129,0.35),
              0 0 36px rgba(16,185,129,0.7);
          }
        }
        @keyframes spotlight-label-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="demo-spotlight"] { animation: none !important; }
        }
      `}</style>
    </>
  );
}
